import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Icon from 'react-feather';
import { Offcanvas, OffcanvasHeader, OffcanvasBody } from 'reactstrap';

import {
  THEME_PRESETS,
  THEME_GROUPS,
  readTheme,
  writeTheme,
  IS_THEME_PICKER_ON
} from '../utils/theme';

// Agent-facing colour-preset picker: a gear tab moulded into the right edge of the screen that
// opens a drawer of fixed swatches, grouped Solid / Gradient / Multi-colour.
// Gated by REACT_APP_THEME_PICKER — with the flag off this renders nothing at all, so the
// trigger is absent from the DOM rather than merely hidden.
//
// Deliberately NOT a colour picker. See the note in src/utils/theme.js: every preset's chrome
// has to clear 4.5:1 against white because ~107 JSX sites hardcode className="text-white", and
// no runtime token can reach a literal class name.
//
// The drawer's own surface stays white in every preset. That is intentional — swatches have to
// be judged against a neutral ground, and a panel that recoloured itself while you were choosing
// a colour would be unreadable in half the presets. Only the SELECTED tile follows the accent.
const ThemePicker = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [active, setActive] = useState(readTheme);

  if (!IS_THEME_PICKER_ON) return null;

  const toggle = () => setIsOpen((prev) => !prev);

  // Apply on click with no confirm step — the point is to see the change instantly, and the
  // drawer stays open so presets can be compared back to back.
  const choose = (id) => setActive(writeTheme(id));

  return (
    <>
      {/* A fixed tab on the right edge, not a header button — the customizer pattern most admin
          panels use. Hidden while the drawer is open: the drawer slides in from the same edge,
          so the tab would otherwise sit under its backdrop half-visible. */}
      <button
        type="button"
        onClick={toggle}
        aria-label={t('appearance')}
        title={t('appearance')}
        className={`cx-theme-trigger ${isOpen ? 'is-hidden' : ''}`}>
        <Icon.Settings size={18} />
      </button>

      <Offcanvas isOpen={isOpen} toggle={toggle} direction="end" className="cx-theme-drawer">
        <OffcanvasHeader toggle={toggle}>{t('appearance')}</OffcanvasHeader>
        <OffcanvasBody>
          <p className="cx-theme-hint">{t('themeHint')}</p>

          {THEME_GROUPS.map((group) => {
            const presets = THEME_PRESETS.filter((p) => p.group === group.id);
            if (!presets.length) return null;
            return (
              <section key={group.id} className="cx-theme-group">
                <h6 className="cx-theme-group__label">{group.label}</h6>
                {/* A grid, not a list: twelve presets as twelve full-width rows would scroll,
                    and comparing two swatches matters more here than reading two labels. */}
                <div className="cx-theme-grid">
                  {presets.map((preset) => {
                    const isActive = preset.id === active;
                    return (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => choose(preset.id)}
                        aria-pressed={isActive}
                        className={`cx-theme-tile ${isActive ? 'is-active' : ''}`}>
                        {/* The preset carries its whole swatch value — a flat colour for a solid
                            preset, the full gradient otherwise — so a three-stop preset shows all
                            three hues instead of silently dropping its middle one. */}
                        <span
                          className="cx-theme-tile__chip"
                          style={{ background: preset.swatch }}>
                          {isActive && <Icon.Check size={16} strokeWidth={3} />}
                        </span>
                        <span className="cx-theme-tile__label">{preset.label}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </OffcanvasBody>
      </Offcanvas>
    </>
  );
};

export default ThemePicker;
